import {
  CountryMarketAccessLifecycle,
  CountryPartyType
} from "../../../../../api/Api";

/**
 * Market Access decides which parties a country setup depends on, and that decision is the whole
 * input to Compliance, Legal, Treasury and Finance. This file is the single reading of it.
 *
 * There is no per-block picker on Market Access. A party the country depends on has to be screened
 * and contracted with, so marking it needed is enough. Where a department genuinely has nothing to
 * do for one, it marks that row "not needed" itself, which is its call to make rather than Market
 * Access's.
 *
 * Rows key on the party type, so a country has at most one TSO, one exchange and so on. See
 * docs/country-additions-plan.md for why that was chosen over an id-based stakeholder list.
 */

export const COUNTRY_PARTY_LABELS: Record<CountryPartyType, string> = {
  tso: "TSO",
  exchange: "Exchange",
  bank: "Bank",
  clearing_bank: "Clearing bank",
  license_authority: "License authority",
  legal_consultant: "Legal consultants"
};

/** Treasury only ever deals with the two banking parties. */
export const TREASURY_PARTY_TYPES: CountryPartyType[] = [
  "bank",
  "clearing_bank"
];

/**
 * Parties Compliance screens. A license authority is a regulator and a legal consultancy is our own
 * adviser, so neither is a counterparty we onboard: there is nothing to KYC or sanction screen
 * either way. They are excluded on the way in rather than left for Compliance to mark "not needed"
 * on every country. Legal and Finance still see them, they do have contracts and invoices.
 */
export const COMPLIANCE_PARTY_TYPES: CountryPartyType[] = [
  "tso",
  "exchange",
  "bank",
  "clearing_bank"
];

/**
 * Parties collateral can be posted towards. A license authority and a law firm cannot hold
 * collateral, so they are excluded on the way in rather than left for Treasury to mark "not needed"
 * on every country.
 */
export const COLLATERAL_PARTY_TYPES: CountryPartyType[] = [
  "tso",
  "exchange",
  "bank",
  "clearing_bank"
];

export type CountryParty = {
  type: CountryPartyType;
  /** The name Market Access typed in, if they have got that far. */
  name?: string;
};

const NEEDED = "needed";

/**
 * The parties Market Access has marked as needed, in a stable order. A party counts as selected on
 * the toggle alone: the name box is often filled in later, so requiring it would hide the party from
 * every downstream lifecycle until someone went back to Market Access.
 */
export const getSelectedParties = (
  marketAccess?: CountryMarketAccessLifecycle
): CountryParty[] => {
  if (!marketAccess) {
    return [];
  }

  const selected: CountryParty[] = [];

  const add = (
    type: CountryPartyType,
    needed?: string,
    name?: string | null
  ) => {
    if (needed === NEEDED) {
      selected.push({ type, name: name ?? undefined });
    }
  };

  add("tso", marketAccess.tso, marketAccess.tsoName);
  add("exchange", marketAccess.exchange, marketAccess.exchangeName);
  add("bank", marketAccess.bank, marketAccess.bankName);
  add(
    "clearing_bank",
    marketAccess.clearingBank,
    marketAccess.clearingBankName
  );
  add(
    "license_authority",
    marketAccess.tradingLicense,
    marketAccess.licenseAuthorityName
  );
  add(
    "legal_consultant",
    marketAccess.legalConsultancy,
    marketAccess.legalConsultantsName
  );

  return selected;
};

/** Every needed party, as bare types. What Compliance, Legal and Finance build their rows from. */
export const selectedPartyTypes = (
  marketAccess?: CountryMarketAccessLifecycle
): CountryPartyType[] => getSelectedParties(marketAccess).map((p) => p.type);

/** Needed parties narrowed to those a given lifecycle can act on. */
export const selectedPartyTypesWithin = (
  marketAccess: CountryMarketAccessLifecycle | undefined,
  scope: CountryPartyType[]
): CountryPartyType[] =>
  selectedPartyTypes(marketAccess).filter((type) => scope.includes(type));

/** "TSO (Energinet)" once Market Access has named it, plain "TSO" until then. */
export const partyLabel = (
  type: CountryPartyType,
  marketAccess?: CountryMarketAccessLifecycle
): string => {
  const party = getSelectedParties(marketAccess).find((p) => p.type === type);
  const base = COUNTRY_PARTY_LABELS[type];

  return party?.name ? `${base} (${party.name})` : base;
};

/**
 * Reconciles a stored row collection against the parties Market Access marked needed: drops rows for
 * parties that are gone, adds empty rows for parties that are new, and returns them in party order.
 * Dropping is intentional. A row for a party the country no longer deals with is not data anyone can
 * act on, and leaving it would show Legal a review for a TSO that is not being used.
 */
export const syncPartyRows = <TRow extends { partyType?: CountryPartyType }>(
  rows: TRow[] | undefined,
  selection: CountryPartyType[],
  createRow: (partyType: CountryPartyType) => TRow
): TRow[] =>
  selection.map(
    (partyType) =>
      (rows ?? []).find((row) => row.partyType === partyType) ??
      createRow(partyType)
  );

/** Replaces one row in a collection, matched on party type. */
export const updatePartyRow = <TRow extends { partyType?: CountryPartyType }>(
  rows: TRow[],
  partyType: CountryPartyType,
  patch: Partial<TRow>
): TRow[] =>
  rows.map((row) => (row.partyType === partyType ? { ...row, ...patch } : row));
